import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { canConductAudits, equalsIgnoreCase } from '../../data/scope';
import { getVisibleStores } from '../../data/stores';
import type { AuditRecord } from '../../data/audits';
import { coverWithoutStores, getRecentAudits, summariesCover } from '../../data/districtSummaries';
import {
  getCompletedTasks,
  getOpenTasks,
  type ServerTask,
} from '../../data/tasks';
import type { Store } from '../../data/types';
import { CompletedAuditsList } from '../../ui/audits/CompletedAuditsList';
import { TaskListView } from '../../ui/tasks/TaskListView';
import { getAllStoreStatuses, type StoreStatusRow } from '../../data/storeStatus';
import { getPublishedTemplates, type PublishedTemplate } from '../../data/templates';
import { districtBreakdown } from '../../audit/gradeBreakdown';
import { buildStoreRows, byUrgency } from '../../data/dashboard';
import { BoardTable, DistrictGrades, GradeHelp, type GradesState } from '../../ui/dashboard/DashboardTables';
import { CompleteTaskModal } from '../../ui/tasks/CompleteTaskModal';
import { CsvButton } from '../../ui/CsvButton';
import { BOARD_CSV_HEADER, boardCsvRows } from '../../data/dashboardCsv';
import { withOwnerHeader } from '../../reports/ownerColumn';
import { ReopenTaskModal } from '../../ui/tasks/ReopenTaskModal';
import { PersonName } from '../../ui/PersonName';
import { OwnerSelect, useOwnerFilter } from '../../ui/reports/OwnerFilter';

/**
 * District rollup: the district's graded store board (Phase 6a — the
 * executive drill-down from the home page's district table), then its
 * audits and tasks, scoped to what the
 * signed-in user can see, with the same filters and sort as the Audits
 * and Tasks hubs (tasks can be grouped). Individual stores are reached by clicking a
 * store name on any card. Reached by clicking a district name anywhere.
 */
export function DistrictPage() {
  const { district } = useParams<{ district: string }>();
  const { user, summaryDistricts, impersonating } = useAppUser();
  /** Some stale monthly summaries are still being rebuilt. */
  const [refreshing, setRefreshing] = useState(false);
  const [districtStores, setStores] = useState<Store[] | null>(null);
  // The Owner filter (2026-10-28) narrows the district's stores — the board, grade, audits and tasks follow.
  const ownerFilter = useOwnerFilter();
  const stores = useMemo(() => (districtStores === null ? null : ownerFilter.apply(districtStores)), [districtStores, ownerFilter]);
  const [audits, setAudits] = useState<AuditRecord[] | null>(null);
  const [tasks, setTasks] = useState<ServerTask[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<ServerTask | null>(null);
  const [reopening, setReopening] = useState<ServerTask | null>(null);
  const [statuses, setStatuses] = useState<StoreStatusRow[] | null>(null);
  const [templates, setTemplates] = useState<PublishedTemplate[] | null>(null);
  /** A grade input failed to load: show "n/a" rather than a misleading number. */
  const [gradeInputFailed, setGradeInputFailed] = useState(false);

  const districtName = (district ?? '').trim();

  // Open + recent completed together; TaskListView's status filter slices.
  const refreshTasks = () => {
    Promise.all([getOpenTasks(), getCompletedTasks()])
      .then(([open, completed]) => setTasks([...open, ...completed]))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  };

  useEffect(() => {
    if (!user || !districtName) return;
    // Visible = active stores within the user's scope; a district page only
    // ever shows the slice of the district the viewer is allowed to see.
    const mine = getVisibleStores(user).then((all) =>
      all
        .filter((s) => equalsIgnoreCase(s.district.trim(), districtName))
        .sort((a, b) =>
          a.storeNumber.localeCompare(b.storeNumber, undefined, {
            numeric: true,
          }),
        ),
    );
    // A re-run (summaryDistricts arriving after the saved profile, another
    // district) or leaving the page: the older load must not land (QA 2026-10-22).
    let live = true;
    mine
      .then((list) => live && setStores(list))
      .catch((err: unknown) => live && setError(err instanceof Error ? err.message : String(err)));
    // This district's monthly summary when the viewer sees all of it (owner
    // ruling 2026-10-22), else the audits read as before. Only THIS
    // district's stale months are rebuilt (in the background).
    const decided = coverWithoutStores(summaryDistricts, impersonating);
    const show = (r: { audits: AuditRecord[]; refreshing: boolean }) => {
      if (!live) return;
      setAudits(r.audits);
      setRefreshing(r.refreshing);
    };
    (decided !== null ? Promise.resolve(decided) : mine.then((list) => summariesCover(summaryDistricts, list, impersonating)))
      .then((cover) =>
        getRecentAudits(cover, { districts: new Set([districtName.toLowerCase()]), onUpdate: show }),
      )
      .then(show)
      .catch((err: unknown) => live && setError(err instanceof Error ? err.message : String(err)));
    refreshTasks();
    getAllStoreStatuses()
      .then((rows) => live && setStatuses(rows))
      .catch(() => {
        if (!live) return;
        setGradeInputFailed(true);
        setStatuses([]);
      });
    getPublishedTemplates()
      .then((rows) => live && setTemplates(rows))
      .catch(() => {
        if (!live) return;
        setGradeInputFailed(true);
        setTemplates([]);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, districtName, summaryDistricts, impersonating]);

  const storeById = useMemo(
    () => new Map((stores ?? []).map((s) => [Number(s.id), s])),
    [stores],
  );

  const districtAudits = useMemo(() => {
    if (audits === null || stores === null) return null;
    return audits
      .filter((a) => storeById.has(a.storeId))
      .map((a) => ({ audit: a, store: storeById.get(a.storeId)! }));
  }, [audits, stores, storeById]);

  const districtTasks = useMemo(() => {
    if (tasks === null || stores === null) return null;
    return tasks.filter((t) => storeById.has(t.storeId));
  }, [tasks, stores, storeById]);

  /** The district's stores with grades, worst first (no grade last). */
  const gradesState: GradesState = gradeInputFailed
    ? 'unavailable'
    : tasks === null || audits === null
      ? 'loading'
      : 'ready';

  const boardRows = useMemo(() => {
    if (stores === null || statuses === null || templates === null) return null;
    // Home's default order ("most urgent"), so both boards agree (exec F8).
    return buildStoreRows({
      stores,
      statuses,
      templates,
      openTasks: (tasks ?? []).filter((t) => t.status !== 'complete'),
      submittedAudits: audits ?? [],
    }).sort(byUrgency);
  }, [stores, statuses, templates, tasks, audits]);
  const districtGrade = useMemo(
    () =>
      boardRows && gradesState === 'ready'
        ? districtBreakdown(boardRows.map((r) => ({ store: r, breakdown: r.breakdown })))
        : null,
    [boardRows, gradesState],
  );

  if (!districtName) {
    return (
      <div className="page-message">
        <p>No district specified.</p>
        <Link to="/">Back to Home</Link>
      </div>
    );
  }
  if (error) {
    return <div className="page-message error">{error}</div>;
  }
  if (stores === null || !user) {
    return <div className="page-message">Loading district…</div>;
  }
  if (stores.length === 0 && districtStores !== null && districtStores.length > 0) {
    return (
      <div className="page-message">
        <p>
          {ownerFilter.owner === 'none'
            ? `Every store in “${districtName}” has an owner.`
            : `None of “${districtName}”’s stores belong to ${ownerFilter.label.replace(/^Owner /, '') || 'that owner'}.`}
        </p>
        <button type="button" className="btn" onClick={() => ownerFilter.setOwner('')}>
          Show every owner
        </button>
      </div>
    );
  }
  if (stores.length === 0) {
    return (
      <div className="page-message">
        <p>
          No stores you have access to are in “{districtName}” (or the
          district doesn’t exist).
        </p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }

  const canManage = canConductAudits(user.role);

  return (
    <div className="dash-page">
      <div className="page-head">
        <div>
          <h1>{districtName}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {stores.length} store{stores.length > 1 ? 's' : ''}
            {(stores[0]?.dmUpn ?? '').trim() ? (
              <>
                {' '}· DM: <PersonName upn={stores[0]!.dmUpn} />
              </>
            ) : (
              stores[0]?.dmName && ` · DM: ${stores[0].dmName}`
            )}
          </p>
        </div>
        {districtGrade && (districtGrade.timeliness.grade !== null || districtGrade.quality.grade !== null) && (
          <div className="dash-district-grade" title="Averages of the district’s stores">
            <DistrictGrades
              district={districtName}
              breakdown={districtGrade}
              stores={boardRows?.length ?? 0}
              behind={boardRows?.filter((r) => r.status === 'overdue').length ?? 0}
            />
          </div>
        )}
      </div>

      {ownerFilter.enabled && (
        <div className="list-tools" role="group" aria-label="Filters">
          <OwnerSelect filter={ownerFilter} />
        </div>
      )}
      <div className="page-head dash-heading">
        <h2 style={{ margin: 0 }}>Stores</h2>
        {boardRows !== null && boardRows.length > 0 && gradesState !== 'loading' && (
          <CsvButton
            name={`${district ?? ''} stores`}
            header={withOwnerHeader(BOARD_CSV_HEADER, ownerFilter.ownerOf)}
            rows={() => boardCsvRows(boardRows, new Date(), gradesState === 'ready', ownerFilter.ownerOf)}
          />
        )}
      </div>
      <GradeHelp />
      {gradeInputFailed && (
        <p className="attr-warning">Grades are unavailable right now — some data couldn’t be loaded. Refresh to try again.</p>
      )}
      {refreshing && !gradeInputFailed && (
        <p className="attr-warning">Some months are being refreshed — grades may change after a reload.</p>
      )}
      {boardRows === null ? <p>Loading…</p> : <BoardTable rows={boardRows} now={new Date()} gradesState={gradesState} />}

      {/* Tasks before the audit history — the part people act on (UI review U74). */}
      <h2 style={{ marginTop: '1.5rem' }}>Tasks</h2>
      <TaskListView
        tasks={districtTasks}
        filterStores={stores}
        storeById={storeById}
        canManage={canManage}
        onComplete={user.role === 'Executive' ? undefined : setCompleting}
        onReopen={(t) => setReopening(t)}
      />

      <h2 style={{ marginTop: '1.5rem' }}>Completed audits</h2>
      <p className="muted" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        The last 13 months — older audits are on each store’s page.
      </p>
      <CompletedAuditsList
        items={districtAudits}
        filterStores={stores}
        context="district"
      />

      {reopening && (
        <ReopenTaskModal
          task={reopening}
          onClose={() => setReopening(null)}
          onDone={() => {
            setReopening(null);
            refreshTasks();
          }}
        />
      )}
      {completing && (
        <CompleteTaskModal
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            refreshTasks();
          }}
        />
      )}
    </div>
  );
}
